import { IComponentOptions, IController } from "angular";
import { IEnrichedEmployee } from "@gtmhub/employees";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { localize } from "@gtmhub/localization";
import { IEnrichedTeam } from "@gtmhub/teams";
import { roundFloatAtMost2DecPlaces } from "@gtmhub/util";
import dayjs from "@webapp/shared/libs/dayjs";
import { IGenericListCardStats, IListItem } from "../../components/generic-list-card/generic-list-card";
import { IAvgCompetitionTimeForMemberTasks, ITeamTasksStats, IUserTasksStats } from "../../models/models";
import { ProfileService } from "../../services/profileService";
import { IGenericCardStats, IStat } from "../generic-card/generic-card.component";

type ITaskOwnership = {
  employee: {
    total: number;
    todo: number;
    inProgress: number;
    completed: number;
  };
  company: number;
  team: number;
};

export class TasksSectionCtrl implements IController {
  target: IEnrichedEmployee | IEnrichedTeam;
  taskOwnership: ITaskOwnership;
  completedTasksRate: IGenericCardStats;
  avgTaskCompletionTime: IGenericCardStats | IGenericListCardStats;
  indicators: {
    loading: boolean;
  };
  teamTasksStats: IGenericListCardStats;

  static $inject = ["ProfileService", "UIErrorHandlingService"];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(private profileService: ProfileService, private uiErrorHandlingService: UIErrorHandlingService) {
    this.indicators = {
      loading: true,
    };
  }

  $onInit(): void {
    this.profileService.getTaskStats(this.target.type, this.target.id).then(
      (tasksStats) => {
        if (this.target.type === "user") {
          this.setUserStats(tasksStats as IUserTasksStats);
        } else {
          this.setTeamStats(tasksStats as ITeamTasksStats);
        }
        this.indicators.loading = false;
      },
      (error) => {
        this.uiErrorHandlingService.handleModal(error);
        this.indicators.loading = false;
      }
    );
  }

  private setTeamStats(tasksStats: ITeamTasksStats): void {
    this.teamTasksStats = {
      main: !tasksStats.overallAvgCompletionTimeForMemberTasks
        ? { suffix: localize("there_are_no_completed_tasks_for_this_team") }
        : this.getCompletionTimeValueAndSuffix(tasksStats.overallAvgCompletionTimeForMemberTasks),
      company: !tasksStats.avgCompletionTimeForAllTasks
        ? { suffix: localize("there_are_no_completed_tasks") + " " }
        : this.getCompletionTimeValueAndSuffix(tasksStats.avgCompletionTimeForAllTasks),
      list: this.setTeamList(tasksStats.avgCompletionTimeForMemberTasks),
    };
  }

  private setTeamList(stats: IAvgCompetitionTimeForMemberTasks[]): IListItem[] {
    return stats.map((memberStat) => {
      return { ...this.getCompletionTimeValueAndSuffix(memberStat.avgCompletionTime), assignee: memberStat.assignee };
    });
  }

  private setUserStats(tasksStats: IUserTasksStats): void {
    this.setTaskOwnership(tasksStats);
    this.setCompletedTasksRate(tasksStats);
    this.setAvgTaskCompletionTime(tasksStats);
  }

  private setTaskOwnership(tasksStats: IUserTasksStats): void {
    const stats = tasksStats.taskOwnershipStats;
    const mainTodo = stats.ownedByUserCount.find((stat) => stat.status === "todo")?.count || 0;
    const mainInProgress = stats.ownedByUserCount.find((stat) => stat.status === "inprogress")?.count || 0;
    const mainDone = stats.ownedByUserCount.find((stat) => stat.status === "done")?.count || 0;
    const mainTotal = mainTodo + mainInProgress + mainDone;
    const companyTotal = stats.allTasksCount.reduce((acc, stat) => acc + stat.count, 0);
    const teamTotal = stats.ownedByUserTeamsCount.reduce((acc, stat) => acc + stat.count, 0);

    this.taskOwnership = {
      employee: {
        total: mainTotal,
        todo: mainTodo,
        inProgress: mainInProgress,
        completed: mainDone,
      },
      company: companyTotal,
      team: teamTotal,
    };
  }

  private setCompletedTasksRate(tasksStats: IUserTasksStats): void {
    const stats = tasksStats.completedTasksRate;
    this.completedTasksRate = {
      main: { value: roundFloatAtMost2DecPlaces(stats.userTasksRate * 100), suffix: "%" },
      company: { value: roundFloatAtMost2DecPlaces(stats.allTasksRate * 100), suffix: "%" },
      team: { value: roundFloatAtMost2DecPlaces(stats.userTeamsTasksRate * 100), suffix: "%" },
    };
  }

  private setAvgTaskCompletionTime(tasksStats: IUserTasksStats): void {
    const stats = tasksStats.avgTaskCompletionTime;

    this.avgTaskCompletionTime = {
      main: this.getCompletionTimeValueAndSuffix(stats.avgCompletionTimeForUserTasks),
      company: this.getCompletionTimeValueAndSuffix(stats.avgCompletionTimeForAllTasks),
      team: this.getCompletionTimeValueAndSuffix(stats.avgCompletionForUserTeamsTasks),
    };
  }

  private getCompletionTimeValueAndSuffix(value: number): IStat {
    const duration = dayjs.duration(value, "milliseconds");
    const durationInDays = duration.asDays();
    const actualDuration = Math.round(durationInDays < 1 ? duration.asHours() : durationInDays);
    let suffix = durationInDays < 1 ? "hour" : "day";
    suffix = actualDuration === 1 ? localize(suffix) : localize(`${suffix}s`);

    return { value: actualDuration, suffix: ` ${suffix}` };
  }
}

export const TasksSectionComponent: IComponentOptions = {
  bindings: {
    target: "<",
  },
  controller: TasksSectionCtrl,
  template: require("./tasks-section.component.html"),
};
